import React, { useEffect, useState } from 'react';
import {
    FormColorFormField,
    FormRadioField,
    FormTextFormField,
    SubmitCancelButtons,
} from 'elements';
import { useForm } from 'react-hook-form';
import { Form, Message } from 'semantic-ui-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import styled from 'styled-components';
import { FileUploadPicker } from 'components/FileUploadPicker';
import {
    AcceptFileType,
    CompanyThemeService,
    MediaService,
    SettingsText,
    ThemeRadioIds,
    ThemeRadioOptions,
} from 'utility';
import { GetCompnayTheme, useNotification } from 'hooks';
import { useAuth } from 'context';

const Page = styled.div`
    color: #1c1917;

    @media (max-width: 720px) {
        .ui.form .fields {
            flex-direction: column !important;
        }

        .ui.form .fields > .field {
            width: 100% !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
        }
    }
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const Card = styled.section`
    background: #fff;
    border: 1px solid #e7e5e4;
    border-radius: 14px;
    padding: 18px 18px 8px;
    min-width: 0;
`;

const Title = styled.h3`
    margin: 0 0 4px;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.02em;
`;

const Lead = styled.p`
    margin: 0 0 14px;
    color: #78716c;
    font-size: 13px;
    line-height: 1.4;
`;

const WideCard = styled(Card)`
    margin-top: 14px;
`;

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 4px;
    padding-right: 76px;

    @media (max-width: 720px) {
        padding-right: 0;
        padding-bottom: 72px;
    }
`;

const QuestionSchema = yup.object().shape({
    TableRowBackgroundColor: yup.string().nullable(),
    HeaderImagePath: yup.array().nullable(),
    FooterImagePath: yup.array().nullable(),
    TableRowBorderColor: yup.string().nullable(),
    HeaderText: yup.string().nullable(),
    FooterText: yup.string().nullable(),
});

export const ThemeScreen = () => {
    const { data } = GetCompnayTheme();
    const [processing, setProcessing] = useState(false);
    const { user } = useAuth();
    const { successToast } = useNotification()
    const {
        control,
        setValue,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(QuestionSchema),
    });

    const FooterImagePath = watch('FooterImagePath');
    const HeaderImagePath = watch('HeaderImagePath');
    const DefaultHeader = watch('DefaultHeader');
    const DefaultFooter = watch('DefaultFooter');

    useEffect(() => {
        if (data?.ThemeId) {
            reset({
                ...data,
                DefaultHeader: data.DefaultHeader || ThemeRadioIds.Image,
                DefaultFooter: data.DefaultFooter || ThemeRadioIds.Image,
                FooterText: data.FooterText || "",
                HeaderText: data.HeaderText || "",
            });
            setValue('FooterImagePath', [{ url: data.FooterImagePath, preview: true }]);
            setValue('HeaderImagePath', [{ url: data.HeaderImagePath, preview: true }]);
        } else {
            reset({
                TableRowBackgroundColor: '',
                DefaultHeader: ThemeRadioIds.Image,
                DefaultFooter: ThemeRadioIds.Image,
            });
        }
    }, [data]);

    const submit = async (formData) => {
        setProcessing(true);
        let submitedData = { ...formData };
        try {
            const allFiles = await Promise.all(
                [formData.HeaderImagePath?.[0], formData.FooterImagePath?.[0]].map(async (ele) => {
                    let result = ele?.url;
                    if (!ele?.preview && ele) {
                        result = await MediaService.uploadMedia({ media: ele.file });
                        result = result.ImagePathUrl;
                    }
                    return result;
                }),
            );
            submitedData = {
                ...submitedData,
                HeaderImagePath: allFiles[0],
                FooterImagePath: allFiles[1],
            };
            if (submitedData.ThemeId) {
                await CompanyThemeService.update(submitedData);
                successToast(SettingsText.themeUpdated)
            } else {
                await CompanyThemeService.add({ ...submitedData, CompanyId: user.CompanyId });
                successToast(SettingsText.themeAdded)
            }
        } catch (error) {
            console.log('error:', error);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Page>
            <Form noValidate onSubmit={handleSubmit(submit)}>
                <Grid>
                    <Card>
                        <Title>Header</Title>
                        <Lead>Image or text shown at the top of exported reports.</Lead>
                        <Form.Field>
                            <FormRadioField
                                control={control}
                                name="DefaultHeader"
                                defaultValue={false}
                                label="Header type"
                                options={ThemeRadioOptions}
                                row
                            />
                        </Form.Field>
                        <Form.Field>
                            {DefaultHeader === ThemeRadioIds.Image ? (
                                <FileUploadPicker
                                    maxFiles={1}
                                    label="Header image"
                                    maxFileSize={500}
                                    accept={AcceptFileType.image}
                                    addedNewFiles={(file) => setValue('HeaderImagePath', file)}
                                    files={HeaderImagePath || []}
                                    fullview
                                />
                            ) : (
                                <FormTextFormField
                                    control={control}
                                    label="Header text"
                                    name="HeaderText"
                                    defaultValue=""
                                    errors={errors.HeaderText}
                                />
                            )}
                        </Form.Field>
                    </Card>
                    <Card>
                        <Title>Footer</Title>
                        <Lead>Image or text shown at the bottom of exported reports.</Lead>
                        <Form.Field>
                            <FormRadioField
                                control={control}
                                name="DefaultFooter"
                                defaultValue={false}
                                label="Footer type"
                                options={ThemeRadioOptions}
                                row
                            />
                        </Form.Field>
                        {DefaultFooter === ThemeRadioIds.Image ? (
                            <FileUploadPicker
                                maxFiles={1}
                                label="Footer image"
                                maxFileSize={500}
                                accept={AcceptFileType.image}
                                addedNewFiles={(file) => setValue('FooterImagePath', file)}
                                files={FooterImagePath || []}
                                fullview
                            />
                        ) : (
                            <FormTextFormField
                                control={control}
                                label="Footer text"
                                name="FooterText"
                                defaultValue=""
                                errors={errors.FooterText}
                            />
                        )}
                    </Card>
                </Grid>
                <WideCard>
                    <Title>Table rows</Title>
                    <Lead>Colors used for the rows in exported report tables.</Lead>
                    <Form.Group widths="equal">
                        <FormColorFormField
                            control={control}
                            label="Row border color"
                            name="TableRowBorderColor"
                            defaultValue={undefined}
                            errors={errors.TableRowBorderColor}
                        />
                        <FormColorFormField
                            control={control}
                            label="Row background color"
                            name="TableRowBackgroundColor"
                            defaultValue={undefined}
                            errors={errors.TableRowBackgroundColor}
                        />
                    </Form.Group>
                </WideCard>
                {Object.keys(errors).length > 0 && (
                    <Message
                        error
                        header="There were some errors with your submission"
                        list={Object.values(errors).map((error) => error.message)}
                    />
                )}
                <Actions>
                    <SubmitCancelButtons loading={processing} />
                </Actions>
            </Form>
        </Page>
    );
};
